/* eslint-disable @typescript-eslint/naming-convention */
import { inject, Injectable, makeStateKey, TransferState } from '@angular/core';
import {
  SfNetworkingService,
  SfLocalizeService,
  SfModel
} from '@app/sf-lib/public_api';
import {  Observable, of, map, catchError, throwError } from 'rxjs';
import { NetworkService } from '@app/core/networking/services/network.service';
import { PrediggoService } from '@app/core/prediggo/services/prediggo.service';
import { PrediggoItemsInfoResponse } from '@app/core/prediggo/model/prediggo-items-info-response.model';
import { PrediggoSimplePageContent } from '@app/core/prediggo/model/prediggo-simple-page-content.model';
import { PrediggoPageEnum } from '@app/core/prediggo/model/prediggo-page.enum';
import { PrediggoParameters } from '@app/core/prediggo/model/prediggo-parameters.model';
import { PrediggoSimplePageContentResponse } from '@app/core/prediggo/model/prediggo-simple-page-content-response.model';
import { themes } from '@assets/mock/themes.mock';
import { PrediggoFacetGroup } from '@app/core/prediggo/model/prediggo-facet-group.model';
import { SomRecipePage } from '@app/core/models/recipe-page.model';
import { SomRecipeListPage } from '@app/core/models/recipe-list-page.model';
import { TranslateService } from '@ngx-translate/core';
import { prediggoTranslation } from '@assets/prediggoObject';
import { SomRecipeArticleDetails } from '@app/core/models/recipe-article-details.model';
import { HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '@env/environment';
import { routes } from '@app/route/routes';

const STATEKEY_THEMES_LIST: any = makeStateKey<any>('recipesThemes');

@Injectable({
  providedIn: 'root'
})
export class RecipeService {

  private somQuery: PrediggoSimplePageContent;
  public allowedFacets = ['recipeTheme', 'recipeDish', 'mainVegetable', 'season'];

  private readonly network = inject(SfNetworkingService);
  private readonly somNetwork = inject(NetworkService);
  private readonly localize = inject(SfLocalizeService);
  private readonly prediggo = inject(PrediggoService);
  private readonly transferState = inject(TransferState);
  private readonly translate = inject(TranslateService);

  public constructor() {
    this.somQuery = PrediggoSimplePageContent.create();
    this.somQuery.parameters = PrediggoParameters.create();
    this.somQuery.parameters.resultsPerPage = 7;
    this.somQuery.parameters.page = 1;
  }

  public getPageTheme(theme: string): Observable<SomRecipeListPage> {
    const themes = this.transferState.get(STATEKEY_THEMES_LIST, null);

    if (themes) {
      const page = themes.find(t => t.theme?.theme_id === this.findThemeId(theme)) || themes[0];
      return of(page);
    }

    return this.network.get(`${environment.apiUrlMultisite}${routes.recipe.list}`).pipe(
      map((pages: any[]) => {
        if (themes === null) {
          this.transferState.set(STATEKEY_THEMES_LIST, pages)
        }

        const page = pages.find(t => t.theme?.theme_id === this.findThemeId(theme));
        return page || '_none';
      })
    )
  }


  public fetchRecipes(nbByPage: number, theme?: string, page?: number, facets?: Record<string, string | string[]>, sorting?: string): Observable<PrediggoSimplePageContentResponse> {
    const stateKey = makeStateKey<PrediggoSimplePageContentResponse>(
      `recipes_page_${page || 1}_${theme || ''}_${JSON.stringify(facets) || ''}_${sorting || ''}`
    );
    
    const recipesList = this.transferState.get(stateKey, null);

    if (recipesList) {
      return of(recipesList);
    }
    const query = this.preparePrediggoProductsListQuery(nbByPage, theme, page, facets, sorting);

    return this.prediggo.simplePageContent(query).pipe(
      map(response => {
        this.transferState.set(stateKey, response); 
        return response
      })
    );
  }

  private preparePrediggoProductsListQuery(
    nbByPage: number, 
    theme: string, 
    page: number = 1, 
    facets?: Record<string, string | string[]>,
    sorting?: string
  ): PrediggoSimplePageContent {
    const query = PrediggoSimplePageContent.create();
    query.pageId = PrediggoPageEnum.navigation_produits_recettes;
    const themeId = this.findThemeId(theme);
    const recipeTheme = themeId !== "_none" ? themeId : undefined;
    query.parameters = {
      filters: {
        itemType: 'CONTENT_1',
        ...(theme  && { recipeTheme }),
        ...facets
      },
      resultsPerPage: nbByPage,
      ...( { page }),
      ...(sorting && { sortingCode: sorting })
    };
  
    return query;
  }

  public facetIsAllowed(facet: [string, string | string[]]): boolean {
    const key = facet[0];
    const values = facet[1];
    if (this.allowedFacets.indexOf(key) < 0) {
      return false;
    }
    if (Array.isArray(values) && values.length > 1) {
      return false;
    }
    return true;
  }


  public fetchRecipeArticle(id: string): Observable<SomRecipeArticleDetails> {
    let options: { headers?: HttpHeaders, params?: HttpParams } = {};
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.set('recipeid', id);
    options = { headers: headers };

    const stateKey = makeStateKey<SomRecipeArticleDetails>(
      `recipe_${id}_articles}`
    );
    const articles = this.transferState.get(stateKey, null);

    if (articles) {
      return of(articles)
    }
    return this.network.get(`${this.somNetwork.apiUrl}${routes.recipe.articles}`, options).pipe(
      map(response => {
        this.transferState.set(stateKey, response);
        return response
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    ) as Observable<SomRecipeArticleDetails>
  }

  /**
   * return the themeId got from the mock with the proper themeName
   * @param {string} themeName
   * @returns {string}
   * @memberof RecipeService
   */
  public findThemeId(themeName: string): string {
    const langThemes = themes.find(t => {
      return t.lang.country === this.localize.currentLang.country &&
        t.lang.code === this.localize.currentLang.code;
    });
    if (langThemes && langThemes.RecipeThemes) {
      for (const theme of langThemes.RecipeThemes) {
        if (theme.name === themeName) {
          return theme.id;
        }
      }
    }
    return '_none';
  }


  // =============== Prediggo Calls ================================


  public fetchRecipe(id: string): Observable<PrediggoItemsInfoResponse> {
    const stateKey = makeStateKey<PrediggoItemsInfoResponse>(
      `recipe_page_${id}}`
    );
    const recipe = this.transferState.get(stateKey, null);

    if (recipe) {
      return of(recipe)
    }

    // Hack: Some items does not respect uppercase ID, fetch them anyway with [id, id.toLowerCase()]
    return this.prediggo.itemsInfo([id.toUpperCase(), id.toLowerCase()]).pipe(
      map(response => {
        this.transferState.set(stateKey, response)
        return response
      })
    )
  }

  public fetchPrediggoBlocks(id: string, isLegumiz: boolean = false): Observable<PrediggoSimplePageContentResponse> {
    const query: PrediggoSimplePageContent = PrediggoSimplePageContent.create();
    query.pageId = isLegumiz ? PrediggoPageEnum.recipe_legumiz : PrediggoPageEnum.page_recette_produits;
    query.parameters = PrediggoParameters.create();
    (query.parameters as any).skus = [id];

    const stateKey = makeStateKey<PrediggoSimplePageContentResponse>(
      `recipe_prediggo_blocks_${id}_legumiz_${isLegumiz}`
    );
    const blocks = this.transferState.get(stateKey, null);

    if (blocks) {
      return of(blocks);
    }

    return this.prediggo.simplePageContent(query).pipe(
      map(response => {
        this.transferState.set(stateKey, response);
        return response
      })
    );
  }

  public printRecipe(print: SomRecipePage): void {
    this.getPrintableRecipe(print)
      .subscribe(
        (res) => {
          if (res && window) {
            window.open(res as string, '_blank', '');
          }
        }
      );
  }

  public getTranslatedFacet(facet: string): string {
    const preddigoObject = prediggoTranslation[this.translate.currentLang.split('_')[1]];
    facet = facet.replaceAll("é", "%E9").replaceAll(" ", "%20")
    const translatedFacet = preddigoObject[facet];
    return translatedFacet || facet;
  }

  public getPrintableRecipe(print: SomRecipePage): Observable<SfModel | SfModel[]> {
    const newObj = SomRecipePage.flatten(print);
    newObj.recipeIngredientsSummary = newObj.recipeIngredientsSummary.replace(/<[^>]+>/g, '');
    newObj.recipeName = print.name
    newObj.translation = {
      'persons': this.translate.instant('MAGAZINE.RECIPE.PEOPLES'),
      'person': this.translate.instant('MAGAZINE.RECIPE.PEOPLE'),
      'ingredients_label': this.translate.instant('MAGAZINE.RECIPE.INGREDIENTS_LABEL'),
      'ingredient_label': this.translate.instant('MAGAZINE.RECIPE.INGREDIENTS_LABEL_ONE'),
      'min': this.translate.instant('MAGAZINE.RECIPE.TIMING.MIN'),
      'step': this.translate.instant('MAGAZINE.RECIPE.STEP'),
      'low_cost': this.translate.instant('RECIPE.BUDGET_LOW'),
      'medium_cost': this.translate.instant('RECIPE.BUDGET_MIDDLE'),
      'high_cost': this.translate.instant('RECIPE.BUDGET_HIGH'),
      'recipe_veggie':this.translate.instant('RECIPE.VEGGIE'),
      'recipe_vegan':this.translate.instant('RECIPE.VEGAN'),
      'recipe_difficulty_easy': this.translate.instant('PREDIGGO.DIFFICULTY.EASY'),
      'recipe_difficulty_medium': this.translate.instant('PREDIGGO.DIFFICULTY.MEDIUM'),
      'recipe_difficulty_hard' : this.translate.instant('PREDIGGO.DIFFICULTY.HARD')
    };
    newObj.location = document.location;
    return this.network.post(`${this.somNetwork.apiUrl}${routes.recipe.pdf}`, newObj)
  }

  // =============== SEO SERVICE ================================

  public seoFilters(filters: PrediggoFacetGroup[], theme?: string): string[] {
    let metaTitle = '';
    let metaLabel = '';
    let metaLabelValue = '';
    let i = 0;
    if (filters && filters.length > 0) {
      filters.forEach(filter => {
        const filterLabel = filter['attributeName'];
        if (filter['options']) {
          filter['options'].forEach(option => {
            // const optionLabel = `${filterLabel} : ${option['label']}`;
            if (option['selected'] && option['selected'] === true) {
              if (i === 0) {
                switch (filterLabel) {
                  case 'pred-recipeTimingPreparation-facet':
                    metaTitle = this.translate.instant('SEO.TITLE.RECIPEPREPARATION', { value: option['label'] });
                    metaLabel = this.translate.instant('SEO.DESCRIPTION.RECIPEPREPARATION', { value: option['label'] });
                    metaLabelValue += (option['label'] + ', ');
                    i++;
                    break;
                  case 'pred-recipeTimingCook-facet':
                    metaTitle = this.translate.instant('SEO.TITLE.RECIPEPREPARATION', { value: option['label'] });
                    metaLabel = this.translate.instant('SEO.DESCRIPTION.RECIPEPREPARATION', { value: option['label'] });
                    metaLabelValue += (option['label'] + ', ');
                    i++;
                    break;
                  case 'pred-recipeCost-Facet':
                    metaTitle = this.translate.instant('SEO.TITLE.RECIPECOST', { value: option['label'] });
                    metaLabel = this.translate.instant('SEO.DESCRIPTION.RECIPECOST', { value: option['label'] });
                    metaLabelValue += (option['label'] + ', ');
                    i++;
                    break;
                  case 'recipeDifficulty':
                    metaTitle = this.translate.instant('SEO.TITLE.RECIPEDIFFICULTY', { value: option['label'] });
                    metaLabel = this.translate.instant('SEO.DESCRIPTION.RECIPEDIFFICULTY', { value: option['label'] });
                    metaLabelValue += (option['label'] + ', ');
                    i++;
                    break;
                  case 'recipeDish':
                    metaTitle = this.translate.instant('SEO.TITLE.RECIPETYPE', { value: option['label'] });
                    metaLabel = this.translate.instant('SEO.DESCRIPTION.RECIPETYPE', { value: option['label'] });
                    metaLabelValue += (option['label'] + ', ');
                    i++;
                    break;
                  case 'recipeDishNumber':
                    metaTitle = this.translate.instant('SEO.TITLE.RECIPEDISH', { value: option['label'] });
                    metaLabel = this.translate.instant('SEO.DESCRIPTION.RECIPEDISH', { value: option['label'] });
                    metaLabelValue += (option['label'] + ', ');
                    i++;
                    break;
                  case 'season':
                    metaTitle = this.translate.instant('SEO.TITLE.RECIPESEASON', { value: option['label'] });
                    metaLabel = this.translate.instant('SEO.DESCRIPTION.RECIPESEASON', { value: option['label'] });
                    metaLabelValue += (option['label'] + ', ');
                    i++;
                    break;
                  case 'mainVegetable':
                    metaTitle = this.translate.instant('SEO.TITLE.RECIPEVEGETABLE', { value: option['label'] });
                    metaLabel = this.translate.instant('SEO.DESCRIPTION.RECIPEVEGETABLE', { value: option['label'] });
                    metaLabelValue += (option['label'] + ', ');
                    i++;
                    break;
                  case 'technologie':
                    metaTitle = this.translate.instant('SEO.TITLE.RECIPEDESC', { value: option['label'] });
                    metaLabel = this.translate.instant('SEO.DESCRIPTION.RECIPEDESC', { value: option['label'] });
                    metaLabelValue += (option['label'] + ', ');
                    i++;
                    break;
                  case 'recipeTheme':
                    metaTitle = this.translate.instant('SEO.TITLE.RECIPETYPE', { value: option['label'] });
                    metaLabel = this.translate.instant('SEO.DESCRIPTION.RECIPETYPE', { value: option['label'] });
                    metaLabelValue += (option['label'] + ', ');
                    i++;
                    break;
                }
              } else if (i > 0 && i <= 2) {
                metaLabelValue += (option['label'] + ', ');
                metaTitle = this.translate.instant('SEO.TITLE.2RECIPEFACETS', { value: metaLabelValue.slice(0, (metaLabelValue.length - 2)) });
                metaLabel = this.translate.instant('SEO.DESCRIPTION.2RECIPEFACETS', { value: metaLabelValue.slice(0, (metaLabelValue.length - 2)) });
                i++;
              } else if (i > 2) {
                if (i > 5) {
                  metaLabelValue += (option['label'] + ', ');
                  metaTitle = this.translate.instant('SEO.TITLE.4RECIPEFACETS', { value: metaLabelValue.slice(0, (metaLabelValue.length - 2)) });
                  metaLabel = this.translate.instant('SEO.DESCRIPTION.4RECIPEFACETS', { value: metaLabelValue.slice(0, (metaLabelValue.length - 2)) });
                }
                i++;
              }

            } else if (i === 0 && theme) {
              metaTitle = theme;
              metaLabel = this.translate.instant('SEO.DESCRIPTION.RECIPES');
            } else if (i === 0 && !theme) {
              metaTitle = this.translate.instant('SEO.TITLE.RECIPES_ALL');
              metaLabel = this.translate.instant('SEO.DESCRIPTION.RECIPES');
            }
          });
        }
      });
    }
    return [metaTitle, metaLabel];
  }
}
